import { differenceInCalendarMonths, isSameYear } from 'date-fns';
import {
  CircleCheckBigIcon,
  CircleDashedIcon,
  CircleDotIcon,
  CircleSlashIcon,
  type LucideIcon,
} from 'lucide-react';

import { useNow } from '@/shared/components/date';
import { useFormatters } from '@/shared/i18n/format';

import type { GoalPriority, GoalStatus } from '../api';

/** Colors of the priorities: important = red, medium = amber, normal = neutral gray (stripe, dots, legend). */
export const PRIORITY_STRIPE: Record<GoalPriority, string> = {
  important: 'bg-destructive',
  medium: 'bg-warning',
  normal: 'bg-priority-normal',
};

/** Minimap and export colors (CSS colors resolved by the browser). */
export const PRIORITY_COLOR: Record<GoalPriority, string> = {
  important: 'var(--destructive)',
  medium: 'var(--warning)',
  normal: 'var(--priority-normal)',
};

export const STATUS_META: Record<
  GoalStatus,
  { icon: LucideIcon; badge: 'secondary' | 'info' | 'success' | 'outline' }
> = {
  notStarted: { icon: CircleDashedIcon, badge: 'secondary' },
  inProgress: { icon: CircleDotIcon, badge: 'info' },
  achieved: { icon: CircleCheckBigIcon, badge: 'success' },
  cancelled: { icon: CircleSlashIcon, badge: 'outline' },
};

/**
 * The deadline as short text: "12 окт", "12 окт, 18:00", "12 окт 2027". `compact` (goal rectangles, ~6 rem): no time,
 * no year within twelve months of today, else a numeric date ("12.10.28") — the full text goes to the tooltip.
 */
export function useDeadlineLabel() {
  const fmt = useFormatters();
  const now = useNow();
  return (deadline: string, hasTime: boolean, compact = false): string => {
    const date = new Date(deadline);
    if (compact)
      return fmt.pattern(date, Math.abs(differenceInCalendarMonths(date, now)) < 12 ? 'd MMM' : 'dd.MM.yy');
    const day = fmt.pattern(date, isSameYear(date, now) ? 'd MMM' : 'd MMM yyyy');
    return hasTime ? `${day}, ${fmt.date(date, 'time')}` : day;
  };
}
